// release-deploy-outcome-manual-resolution.ts
//
// Close-out A5 (Luke Q4, 2026-09-30): `blocked:deploy_outcome_unknown` stops
// the mission because the journal cannot tell whether a deploy ran (process
// died mid-deploy, timeout, signal). Its only exit used to be `archive`, so a
// deploy that really succeeded never became `released`. This records the
// answer of a person who checked the target — for ONE named candidate:
//   - `deployed`     ⇒ `operation_result: succeeded` for that deploy intent;
//     assessDeployOutcome then sees a deploy bound to this candidate + deploy
//     hash, writes the missing receipt and the next `run` runs smoke only;
//   - `not-deployed` ⇒ `operation_result: failed`; the next `run` may deploy
//     again, after the usual G3 recheck and clean-worktree check.
// The original timed_out/killed result stays in the journal; the manual one
// carries `manual_resolution` (actor, candidate, answer, previous outcome).
//
// A manual `deployed` can be taken back (review P2 M4): `not-deployed` for the
// same candidate (and the same deploy hash — an answer still in force) overrides
// it while no smoke receipt exists. The journal only
// grows (another `operation_result`, previous_outcome `succeeded`); a deploy
// receipt `run` already built from the manual answer — it carries
// `manual_resolution` — is removed FIRST, so a crash in between replays to the
// state before the retraction instead of leaving a receipt that outvotes it.
// Once the smoke receipt exists the release is verified and nothing is undone.
//
// Refuses without writing anything when: the candidate is not a full sha, the
// actor is not the mission's bound actor (same rule as gate approval), a `run`
// of this mission is alive (machine lock, and the journal lease behind it),
// the journal is RECOVERING, or — by what the journal says
// (release-deploy-outcome-manual-resolution-targets.ts) —
//   - nothing is unknown and nothing is retractable (`nothing_unknown`), or no
//     unknown deploy was for that candidate (`candidate_mismatch`);
//   - for `deployed`: the deploy was made under a different `config.release`
//     than the current one (`deploy_hash_mismatch`, review P2 H2: that answer
//     would be ignored and the next `run` would deploy again);
//   - for `not-deployed`: the only manual `deployed` answer for the candidate
//     was made under another deploy hash (`manual_answer_not_in_force`, review
//     261001 M1: `run` already deployed again under the new one), or the smoke
//     receipt exists (`smoke_already_passed`).
// The ok-result says whether the manual receipt was removed
// (`deploy_receipt_removed`) and, when a receipt stays, which kind
// (`blocking_deploy_receipt`: `real` or `damaged`) — it, not this answer,
// decides what the next `run` does. An unbound deploy intent (CLI ≤
// 3.33.0-beta.5) names no candidate — `archive` stays its exit.
import { randomUUID } from "node:crypto";
import { JournalLeaseError } from "./journal-lease.js";
import { isBoundActor, readMissionActorBinding } from "./mission-actor-binding.js";
import { readMachineLock } from "./mission-lock.js";
import { MissionStore } from "./mission-store.js";
import {
  type ManualReceiptRemoval,
  removeManualDeployReceipt,
  smokeReceiptExists,
} from "./release-deploy-manual-receipt.js";
import { selectResolutionTargets } from "./release-deploy-outcome-manual-resolution-targets.js";
import {
  type ResolveUnknownDeployInput,
  type ResolveUnknownDeployResult,
  refuse,
} from "./release-deploy-outcome-manual-resolution-types.js";

export type {
  ManualDeployAnswer,
  ResolveUnknownDeployInput,
  ResolveUnknownDeployResult,
} from "./release-deploy-outcome-manual-resolution-types.js";

const FULL_SHA = /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/;

export async function resolveUnknownDeployOutcome(
  input: ResolveUnknownDeployInput,
): Promise<ResolveUnknownDeployResult> {
  const candidate = input.candidateSha.toLowerCase();
  if (!FULL_SHA.test(candidate)) {
    return refuse(
      "candidate_invalid",
      `--candidate phải là sha ĐẦY ĐỦ (40/64 hex) của candidate đã kiểm tay, nhận "${input.candidateSha}".`,
    );
  }
  const binding = await readMissionActorBinding(input.controlRoot);
  if (binding === undefined || !isBoundActor(binding, input.actorId)) {
    return refuse(
      "actor_not_bound",
      `"${input.actorId}" không phải actor gắn với mission ${input.missionId} — chỉ người đó được xác nhận kết quả deploy.`,
    );
  }
  const lock = await readMachineLock(input.lockPath);
  if (lock?.missionId === input.missionId && (await input.liveness.isAlive(lock.pid))) {
    return refuse(
      "lock_held_by_this_mission",
      `Mission ${input.missionId} đang chạy (pid ${lock.pid}) — dừng \`run\` rồi thử lại.`,
    );
  }

  let store: MissionStore;
  try {
    store = await MissionStore.open(input.controlRoot);
  } catch (err) {
    if (!(err instanceof JournalLeaseError)) throw err;
    return refuse(
      "journal_busy",
      `${err.message} — nếu \`run\` đã chết, chạy \`avatar autopilot unlock\` rồi thử lại.`,
    );
  }
  try {
    if (store.status === "RECOVERING") {
      return refuse(
        "journal_recovering",
        `Journal mission ở trạng thái RECOVERING (${store.recoveringReason ?? "?"}) — xử lý theo \`avatar autopilot doctor\` trước.`,
      );
    }
    const selection = selectResolutionTargets({
      state: store.state,
      missionId: input.missionId,
      candidate,
      outcome: input.outcome,
      currentDeployHash: input.currentDeployHash,
    });
    if (!selection.ok) return selection.refusal;
    const { matching, retractable } = selection;

    let receipt: ManualReceiptRemoval = "absent";
    if (retractable.length > 0) {
      if (await smokeReceiptExists(input.controlRoot, candidate)) {
        return refuse(
          "smoke_already_passed",
          `Smoke đã pass cho candidate ${candidate} (release-state/smoke-${candidate}.json) — release đã ` +
            "được xác nhận, không rút lại câu trả lời `deployed` nữa. Muốn bỏ mission ⇒ `avatar autopilot archive`.",
        );
      }
      receipt = await removeManualDeployReceipt(input.controlRoot, candidate);
    }
    const targets = [...matching, ...retractable];
    const at = (input.now ?? (() => new Date()))().toISOString();
    await store.commit(
      targets.map((intent) => ({
        event_id: `${intent.operation_id}-manual-${randomUUID()}`,
        operation_id: intent.operation_id,
        delta: {
          kind: "operation_result",
          operation_id: intent.operation_id,
          outcome: input.outcome === "deployed" ? "succeeded" : "failed",
          manual_resolution: {
            actor: input.actorId,
            candidate_sha: candidate,
            answer: input.outcome,
            previous_outcome: store.state.results[intent.operation_id]?.outcome ?? null,
            via: "avatar autopilot resolve-deploy",
            at,
          },
        },
      })),
    );
    return {
      ok: true,
      mission_id: input.missionId,
      candidate_sha: candidate,
      outcome: input.outcome,
      resolved_operation_ids: targets.map((intent) => intent.operation_id),
      ...(retractable.length > 0 ? { retracted_manual_deployed: true as const } : {}),
      deploy_receipt_removed: receipt === "removed",
      ...(receipt === "real" || receipt === "damaged" ? { blocking_deploy_receipt: receipt } : {}),
    };
  } finally {
    await store.close();
  }
}
